import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as apiLogin, shopperLogin } from '../api/api';
import { auth } from '../config/firebase';
import { signOut } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [shopperToken, setShopperToken] = useState(localStorage.getItem('shopperToken'));
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(localStorage.getItem('userType')); // 'admin' or 'shopper'

  useEffect(() => {
    if (token) {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
        setUserType('admin');
      }
    } else if (shopperToken) {
      const savedShopper = localStorage.getItem('shopper');
      if (savedShopper) {
        setUser(JSON.parse(savedShopper));
        setUserType('shopper');
      }
    }
    setLoading(false);
  }, [token, shopperToken]);

  // Admin login with username/password
  const login = async (email, password) => {
    try {
      const response = await apiLogin(email, password);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('userType', 'admin');
      localStorage.removeItem('shopperToken'); // Clear shopper session
      localStorage.removeItem('shopper');
      
      setToken(token);
      setShopperToken(null);
      setUser(user);
      setUserType('admin');
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  // Shopper login/registration with Firebase token
  const loginShopper = async (firebaseIdToken, shopperInfo) => {
    try {
      const response = await shopperLogin(firebaseIdToken, shopperInfo);
      const { token, user } = response.data;
      
      localStorage.setItem('shopperToken', token);
      localStorage.setItem('shopper', JSON.stringify(user));
      localStorage.setItem('userType', 'shopper');
      localStorage.removeItem('token'); // Clear admin session
      localStorage.removeItem('user');
      
      setShopperToken(token);
      setToken(null);
      setUser(user);
      setUserType('shopper');
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      // Sign out from Firebase if shopper
      if (userType === 'shopper') {
        await signOut(auth);
        localStorage.removeItem('shopperToken');
        localStorage.removeItem('shopper');
      } else {
        // Admin logout
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      
      localStorage.removeItem('userType');
      setToken(null);
      setShopperToken(null);
      setUser(null);
      setUserType(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isAdmin = () => {
    return userType === 'admin' && user?.role === 'admin';
  };

  const isShopper = () => {
    return userType === 'shopper';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        shopperToken,
        loading,
        userType,
        login,
        loginShopper,
        logout,
        isAdmin,
        isShopper
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
